import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Users, Briefcase, Plane, Calendar, LogOut, Eye, ArrowRight, Shield, Server, Lock, Layers, Clock, UserX, Volume2, VolumeX, FileSearch, Brain, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ParticleBackgroundParallax from '../components/ParticleBackgroundParallax';
import TypingAnimationWithAudio from '../components/TypingAnimationWithAudio';
import PowerCardWithAudio from '../components/PowerCardWithAudio';
import DemoForm from '../components/DemoForm';
import CloneCapabilities from '../components/CloneCapabilities';
import AnimatedCounter from '../components/AnimatedCounter';
import RevealText from '../components/RevealText';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const staggerChildren = {
  initial: {},
  whileInView: {
    transition: {
      staggerChildren: 0.15
    }
  },
  viewport: { once: true }
};

const childVariant = {
  initial: { opacity: 0, y: 30, scale: 0.95 },
  whileInView: { 
    opacity: 1, 
    y: 0, 
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const pulseGlow = {
  animate: {
    boxShadow: [
      '0 0 20px rgba(59, 130, 246, 0.2)',
      '0 0 40px rgba(59, 130, 246, 0.4)',
      '0 0 20px rgba(59, 130, 246, 0.2)'
    ]
  },
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut"
  }
};

const LandingPage = () => {
  const [isMuted, setIsMuted] = useState(true);
  const desktopVideoRef = useRef(null);
  const mobileVideoRef = useRef(null);

  const toggleSound = () => {
    if (desktopVideoRef.current) {
      desktopVideoRef.current.muted = !desktopVideoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const scrollToDemo = () => {
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  const problemIcons = [
    { icon: Layers, title: "Dispersée", desc: "Dans 10 outils différents" },
    { icon: Clock, title: "Difficile à retrouver", desc: "Emails, messages, notes, appels..." },
    { icon: UserX, title: "Dépendante des personnes", desc: "Qui oublient, partent, sont occupées" }
  ];

  const independenceItems = [
    { icon: Briefcase, label: "En rendez-vous" },
    { icon: Plane, label: "En déplacement" },
    { icon: Calendar, label: "En congé" },
    { icon: LogOut, label: "Après un départ" }
  ];

  const qaItems = [
    {
      question: "On en est où avec le projet Martin ?",
      answer: "14 échanges en 2 mois. Dernière action : devis envoyé vendredi. Le client doit revenir lundi avec une réponse."
    },
    {
      question: "Il y a des signaux d'alerte sur nos clients ?",
      answer: "Le client Morel montre des signes d'insatisfaction. Ton plus sec, délais de réponse plus longs. Je recommande une relance personnalisée rapidement."
    },
    {
      question: "Rédige une relance pour Morel dans le style de Justine.",
      answer: "Bonjour M. Morel, je reviens vers vous suite à notre échange. Je comprends vos interrogations sur le délai — je m'en occupe personnellement. Bien à vous, Justine."
    }
  ];

  const useCases = [
    {
      title: "Un nouveau collaborateur reprend un dossier",
      before: "2 heures",
      after: "30 secondes"
    },
    {
      title: "Un manager prépare un RDV client",
      before: "Appeler 3 personnes",
      after: "Une question"
    },
    {
      title: "Un directeur veut le point sur un client difficile",
      before: "Réunion de 30 min avec 3 personnes",
      after: "Une question, réponse en 10 secondes"
    }
  ];

  const faqItems = [
    {
      question: "Faut-il changer nos outils ?",
      answer: "Non. Cloneia s'adapte à votre façon de travailler."
    },
    {
      question: "Les équipes doivent-elles faire quelque chose ?",
      answer: "Non. Tout est automatique."
    },
    {
      question: "Ça fonctionne sans CRM ?",
      answer: "Oui. Cloneia capte l'information directement dans vos échanges."
    },
    {
      question: "Combien de temps pour déployer ?",
      answer: "Quelques jours."
    },
    {
      question: "Combien ça coûte ?",
      answer: "Tarification par collaborateur. Détails lors de la démo."
    }
  ];

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="landing-page">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-testid="hero-section">
        {/* Video Background - Desktop only */}
        <div className="absolute inset-0 z-0 hidden md:block">
          <video
            ref={desktopVideoRef}
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-40"
          >
            <source src="/cloneia-hero-video-audio.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/60 via-[#09090b]/40 to-[#09090b]" />
          
          {/* Sound Toggle Button - Desktop */}
          <motion.button
            onClick={toggleSound}
            className="absolute bottom-8 right-8 z-20 p-3 rounded-full bg-[#1a1a2e]/80 border border-white/10 backdrop-blur-sm hover:bg-[#1a1a2e] transition-colors group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            animate={isMuted ? { 
              boxShadow: ['0 0 0 rgba(59, 130, 246, 0)', '0 0 15px rgba(59, 130, 246, 0.5)', '0 0 0 rgba(59, 130, 246, 0)']
            } : {}}
            transition={{ duration: 2, repeat: isMuted ? Infinity : 0 }}
            data-testid="sound-toggle-button"
          >
            {isMuted ? (
              <VolumeX className="w-5 h-5 text-[#a1a1aa] group-hover:text-[#f5f5f5] transition-colors" />
            ) : (
              <Volume2 className="w-5 h-5 text-[#3b82f6]" />
            )}
          </motion.button>
        </div>
        
        <ParticleBackgroundParallax />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#f5f5f5] tracking-tighter leading-none mb-6">
              <RevealText delay={0.3}>Posez une question.</RevealText>
              <br />
              <span className="text-[#3b82f6]">
                <RevealText delay={0.8}>Toute votre entreprise vous répond.</RevealText>
              </span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-lg md:text-xl text-[#f5f5f5] max-w-3xl mx-auto mb-4 font-medium"
          >
            Cloneia crée un clone mémoire de chaque collaborateur et rend cette information accessible à tout moment.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            className="text-base md:text-lg text-[#a1a1aa] max-w-3xl mx-auto mb-10"
          >
            Sans saisie. Sans effort. Sans dépendre des humains.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, delay: 1.6, type: "spring", stiffness: 200 }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={scrollToDemo}
                className="bg-[#3b82f6] hover:bg-[#2563eb] text-white px-8 py-6 rounded-full font-semibold text-lg transition-all glow-blue"
                data-testid="hero-cta-button"
              >
                Demander une démo
              </Button>
            </motion.div>
          </motion.div>

          {/* Mobile Video Player */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.8 }}
            className="mt-8 md:hidden"
          >
            <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#1a1a2e]/50">
              <video
                ref={mobileVideoRef}
                controls
                playsInline
                poster="/cloneia-hero-video-audio.mp4#t=0.1"
                className="w-full aspect-video"
              >
                <source src="/cloneia-hero-video-audio.mp4" type="video/mp4" />
              </video>
            </div>
            <p className="text-xs text-[#71717a] mt-2 text-center">Voir la démo en action</p>
          </motion.div>

          {/* Social proof bar with animated counters */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.8 }}
            className="mt-16 pt-8 border-t border-white/10"
          >
            <div className="flex flex-wrap justify-center gap-8 md:gap-16 text-center">
              <div>
                <p className="text-2xl md:text-3xl font-bold text-[#f5f5f5]">
                  <AnimatedCounter target={20000} duration={2.5} />
                </p>
                <p className="text-sm text-[#71717a]">interactions captées</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-bold text-[#f5f5f5]">
                  <AnimatedCounter target={5000} duration={2} />
                </p>
                <p className="text-sm text-[#71717a]">contacts unifiés</p>
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-bold text-[#f5f5f5]">
                  <AnimatedCounter target={600} duration={1.5} />
                </p>
                <p className="text-sm text-[#71717a]">nouvelles infos / jour</p>
              </div>
            </div>
            <p className="text-xs text-[#71717a] mt-4">Données réelles d'un déploiement en production.</p>
          </motion.div>

          {/* Interrogeable phrase with animation */}
          <motion.p
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 2.2, type: "spring" }}
            className="mt-8 text-xl md:text-2xl font-semibold"
          >
            <motion.span
              animate={{ 
                color: ['#3b82f6', '#60a5fa', '#3b82f6'],
                textShadow: [
                  '0 0 20px rgba(59, 130, 246, 0.5)',
                  '0 0 40px rgba(59, 130, 246, 0.8)',
                  '0 0 20px rgba(59, 130, 246, 0.5)'
                ]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              Votre entreprise devient interrogeable.
            </motion.span>
          </motion.p>
        </div>
      </section>

      {/* Section Clone Mémoire - Mini bloc */}
      <section className="py-16 md:py-20 px-6 md:px-12 border-b border-white/5" data-testid="clone-memory-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeInUp}>
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">Le concept</p>
            <h2 className="text-2xl md:text-3xl font-semibold text-[#f5f5f5] tracking-tight mb-6">
              Un clone mémoire pour chaque collaborateur
            </h2>
            <p className="text-[#a1a1aa] leading-relaxed max-w-2xl mx-auto">
              Chaque collaborateur possède une connaissance issue de ses échanges.
              Cloneia la capte et crée une mémoire exploitable.
            </p>
            <p className="text-[#f5f5f5] mt-4 font-medium">
              Vous pouvez accéder à cette mémoire à tout moment, sans dépendre de la personne.
            </p>

            {/* Memory comparison bars */}
            <div className="mt-12 max-w-md mx-auto space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[#71717a] text-sm">Mémoire humaine</span>
                  <span className="text-[#71717a] text-sm font-medium">~10%</span>
                </div>
                <div className="h-3 bg-[#1a1a2e] rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#71717a] to-[#52525b] rounded-full"
                    initial={{ width: 0 }}
                    whileInView={{ width: '10%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.3 }}
                  />
                </div>
                <p className="text-[#52525b] text-xs mt-1 italic">Oublie, filtre, déforme...</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[#f5f5f5] text-sm font-medium">Clone mémoire</span>
                  <span className="text-[#3b82f6] text-sm font-bold">100%</span>
                </div>
                <div className="h-3 bg-[#1a1a2e] rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-[#3b82f6] to-[#60a5fa] rounded-full"
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                    style={{ boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)' }}
                  />
                </div>
                <p className="text-[#3b82f6] text-xs mt-1 font-medium">Retient tout. Toujours.</p>
              </motion.div>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="text-xl md:text-2xl font-semibold text-center mt-10"
            >
              <span className="text-[#71717a]">La mémoire humaine oublie.</span>
              <br />
              <span className="text-[#f5f5f5]">La mémoire du clone, </span>
              <motion.span
                className="text-[#3b82f6]"
                animate={{
                  textShadow: [
                    '0 0 10px rgba(59, 130, 246, 0.3)',
                    '0 0 20px rgba(59, 130, 246, 0.6)',
                    '0 0 10px rgba(59, 130, 246, 0.3)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                jamais.
              </motion.span>
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Section Capacités du Clone */}
      <CloneCapabilities />

      {/* Section 1 - Le Problème with animated cards */}
      <section id="problem" className="py-24 md:py-32 px-6 md:px-12" data-testid="problem-section">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp}>
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">Le problème</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight mb-12">
              L'information existe.
              <br />
              Mais elle est introuvable.
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
            variants={staggerChildren}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {problemIcons.map((item, index) => (
              <motion.div
                key={index}
                variants={childVariant}
                whileHover={{ 
                  scale: 1.03, 
                  borderColor: 'rgba(59, 130, 246, 0.3)',
                  transition: { duration: 0.2 }
                }}
                className="bg-[#1a1a2e]/50 border border-white/5 rounded-2xl p-6 text-center cursor-default"
              >
                <motion.div
                  animate={{ 
                    y: [0, -5, 0],
                    rotate: index === 0 ? [0, 5, -5, 0] : 0
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: index * 0.3
                  }}
                >
                  <item.icon className="w-10 h-10 text-[#3b82f6] mx-auto mb-4" />
                </motion.div>
                <h3 className="text-xl font-semibold text-[#f5f5f5] mb-2">{item.title}</h3>
                <p className="text-[#71717a] text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.p 
            {...fadeInUp}
            className="text-2xl md:text-3xl font-semibold text-[#f5f5f5] text-center"
          >
            "Vous cherchez plus que vous n'agissez."
          </motion.p>
        </div>
      </section>

      {/* Section 2 - Wow Moment with enhanced terminal */}
      <section id="solution" className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="wow-section">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">La solution</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight">
              Et si vous pouviez juste poser la question ?
            </h2>
          </motion.div>

          <TypingAnimationWithAudio
            question="Mme Dupont a appelé 3 fois cette semaine. Que veut-elle exactement ?"
            answer="Elle conteste la facture du 12 mars (2 270€). Mardi elle a demandé un échéancier à Justine. Mercredi elle a rappelé car elle n'avait pas reçu le mail. Jeudi Justine lui a renvoyé et Mme Dupont a accepté le paiement en 3 fois."
            delay={500}
          />

          <motion.p 
            {...fadeInUp}
            className="text-center text-[#71717a] mt-8"
          >
            Réponse instantanée. Basée sur tous les échanges réels. Sans que personne n'ait rien saisi.
          </motion.p>

          <motion.p 
            {...fadeInUp}
            className="text-center text-[#f5f5f5] font-medium mt-6"
          >
            Posez une question. Obtenez une réponse. Et sachez quoi faire.
          </motion.p>

          <motion.p 
            {...fadeInUp}
            className="text-center text-[#71717a] mt-4 text-sm"
          >
            Posez vos questions par écrit ou à l'oral, comme dans une conversation.
          </motion.p>
        </div>
      </section>

      {/* Section Différenciation with glowing text */}
      <section className="py-16 md:py-20 px-6 md:px-12" data-testid="differentiation-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p 
            {...fadeInUp}
            className="text-2xl md:text-3xl font-semibold text-[#f5f5f5]"
          >
            "Vous n'accédez plus aux outils.
            <br />
            <motion.span 
              className="text-[#3b82f6]"
              animate={{
                textShadow: [
                  '0 0 10px rgba(59, 130, 246, 0.3)',
                  '0 0 30px rgba(59, 130, 246, 0.6)',
                  '0 0 10px rgba(59, 130, 246, 0.3)'
                ]
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              Vous accédez à la connaissance.
            </motion.span>"
          </motion.p>
        </div>
      </section>

      {/* Section 3 - La Puissance with hover effects */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="power-section">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">La puissance</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight mb-6">
              Une question. Toute l'entreprise.
            </h2>
            <p className="text-[#a1a1aa] max-w-2xl mx-auto">
              Cloneia capte automatiquement l'information là où elle se trouve déjà et crée un clone mémoire pour chaque collaborateur.
              Il interroge toute l'information de votre entreprise en même temps.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            variants={staggerChildren}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {qaItems.map((item, index) => (
              <PowerCardWithAudio
                key={index}
                question={item.question}
                answer={item.answer}
                audioId={`power-${index + 1}`}
                index={index}
              />
            ))}
          </motion.div>

          <motion.p 
            {...fadeInUp}
            className="text-2xl md:text-3xl font-semibold text-[#f5f5f5] text-center mt-16"
          >
            "Vous demandez. Vous savez."
          </motion.p>

          <motion.p 
            {...fadeInUp}
            className="text-center text-[#71717a] mt-6"
          >
            Accédez à ce que vos équipes savent.
          </motion.p>

          <motion.p 
            {...fadeInUp}
            className="text-center text-[#a1a1aa] mt-4 max-w-2xl mx-auto text-sm"
            data-testid="comprehension-hint"
          >
            Cloneia ne se contente pas de mémoriser vos échanges. Il en comprend les enjeux et identifie les signaux importants : blocages, tensions, opportunités.
          </motion.p>
        </div>
      </section>

      {/* Section 4 - L'Indépendance with pulsing icons */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="independence-section">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">L'indépendance</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight">
              Votre entreprise ne devrait jamais dépendre
              <br />
              de la disponibilité de quelqu'un.
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12"
            variants={staggerChildren}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {independenceItems.map((item, index) => (
              <motion.div
                key={index}
                variants={childVariant}
                whileHover={{ scale: 1.05 }}
                className="bg-[#1a1a2e]/50 border border-white/5 rounded-2xl p-6 text-center"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    opacity: [0.8, 1, 0.8]
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: index * 0.4
                  }}
                >
                  <item.icon className="w-8 h-8 text-[#3b82f6] mx-auto mb-3" />
                </motion.div>
                <p className="text-[#f5f5f5] font-medium text-sm">{item.label}</p>
                <motion.p 
                  className="text-[#3b82f6] text-xs mt-2"
                  animate={{ opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity, delay: index * 0.2 }}
                >
                  L'info reste là
                </motion.p>
              </motion.div>
            ))}
          </motion.div>

          <motion.p {...fadeInUp} className="text-center text-[#a1a1aa] mb-4">
            Un collaborateur oublie. N'est pas toujours joignable. Peut partir. 
            Avec Cloneia, l'information survit aux personnes.
          </motion.p>

          <motion.p {...fadeInUp} className="text-center text-[#f5f5f5] font-medium mb-8">
            Parce que la mémoire reste accessible, même quand les collaborateurs ne le sont pas.
          </motion.p>

          <motion.p 
            {...fadeInUp}
            className="text-2xl md:text-3xl font-semibold text-[#f5f5f5] text-center"
          >
            "Les gens changent. L'information reste."
          </motion.p>
        </div>
      </section>

      {/* Section 5 - Double Bénéfice */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="benefit-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
              className="bg-[#1a1a2e] border border-white/10 rounded-2xl p-8 md:p-10"
            >
              <div className="flex items-center gap-3 mb-6">
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  <Users className="w-6 h-6 text-[#3b82f6]" />
                </motion.div>
                <span className="text-sm uppercase tracking-[0.15em] text-[#3b82f6] font-bold">Collaborateurs</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-[#f5f5f5] mb-4">
                Arrêtez de chercher.
              </h3>
              <p className="text-[#a1a1aa] leading-relaxed">
                Plus besoin de fouiller dans vos emails, de demander à un collègue, d'interrompre quelqu'un. 
                Vous posez la question, vous avez la réponse.
              </p>
              <p className="text-[#f5f5f5] font-medium mt-4">
                Plus besoin de chercher. Vous savez.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              whileHover={{ 
                scale: 1.02, 
                boxShadow: '0 0 30px rgba(59, 130, 246, 0.2)',
                transition: { duration: 0.2 } 
              }}
              className="bg-[#1a1a2e] border border-[#3b82f6]/30 rounded-2xl p-8 md:p-10"
            >
              <div className="flex items-center gap-3 mb-6">
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Eye className="w-6 h-6 text-[#3b82f6]" />
                </motion.div>
                <span className="text-sm uppercase tracking-[0.15em] text-[#3b82f6] font-bold">Managers / Direction</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-[#f5f5f5] mb-4">
                Voyez tout. Sans rien demander.
              </h3>
              <p className="text-[#a1a1aa] leading-relaxed">
                Vision complète de l'activité en temps réel. Sans attendre les comptes rendus. 
                Sans interrompre les équipes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 6 - Cas Concrets */}
      <section id="use-cases" className="py-24 md:py-32 px-6 md:px-12" data-testid="use-cases-section">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">Cas concrets</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight">
              En situation réelle.
            </h2>
          </motion.div>

          <motion.div 
            className="space-y-6"
            variants={staggerChildren}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {useCases.map((scenario, index) => (
              <motion.div
                key={index}
                variants={childVariant}
                whileHover={{ x: 10, transition: { duration: 0.2 } }}
                className="bg-[#1a1a2e]/50 border border-white/5 rounded-2xl p-6 md:p-8"
              >
                <h3 className="text-lg font-medium text-[#f5f5f5] mb-4">{scenario.title}</h3>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-[#71717a] text-sm">Avant :</span>
                    <span className="text-[#a1a1aa] line-through">{scenario.before}</span>
                  </div>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="w-4 h-4 text-[#3b82f6]" />
                  </motion.div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#71717a] text-sm">Avec Cloneia :</span>
                    <span className="text-[#3b82f6] font-medium">{scenario.after}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.p 
            {...fadeInUp}
            className="text-center text-xl md:text-2xl font-semibold text-[#f5f5f5] mt-12"
          >
            Ce qui prenait 30 minutes prend quelques secondes.
          </motion.p>
        </div>
      </section>

      {/* Section 7 - Disponibilité */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="availability-section">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div {...fadeInUp}>
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">Disponibilité</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight mb-6">
              Disponible partout.
            </h2>
            <p className="text-[#a1a1aa] max-w-2xl mx-auto">
              Cloneia est accessible en application web et mobile. Au bureau, en déplacement, sur le terrain 
              — l'information vous suit.
            </p>
            <p className="text-[#71717a] text-sm mt-6">
              Accédez à ce que vos équipes savent. Interrogez la mémoire de votre entreprise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 8 - Intégration */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="integration-section">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div {...fadeInUp}>
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">Intégration</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight mb-6">
              Sans rien changer.
            </h2>
            <p className="text-[#a1a1aa] max-w-3xl mx-auto leading-relaxed">
              Cloneia se connecte à vos outils existants en quelques heures. Téléphonie. Emails. Messagerie. CRM. 
              Pas de migration. Pas de configuration complexe. Pas de formation. Vos équipes continuent de travailler exactement comme avant.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 9 - Confiance */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="trust-section">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">Sécurité</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight">
              Vos données en sécurité.
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
            variants={staggerChildren}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {[
              { icon: Server, title: "Hébergement en France", description: "Vos données restent chez vous" },
              { icon: Lock, title: "Chiffrement de bout en bout", description: "Aucune donnée accessible en clair" },
              { icon: Shield, title: "Accès contrôlé", description: "Chaque collaborateur ne voit que ce qui le concerne" }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={childVariant}
                whileHover={{ scale: 1.05 }}
                className="text-center p-6"
              >
                <motion.div
                  animate={{ 
                    y: [0, -3, 0],
                    scale: [1, 1.05, 1]
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: index * 0.5
                  }}
                >
                  <item.icon className="w-10 h-10 text-[#3b82f6] mx-auto mb-4" />
                </motion.div>
                <h3 className="text-lg font-medium text-[#f5f5f5] mb-2">{item.title}</h3>
                <p className="text-[#71717a] text-sm">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Section 10 - FAQ */}
      <section id="faq" className="py-24 md:py-32 px-6 md:px-12" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">FAQ</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight">
              Questions fréquentes
            </h2>
          </motion.div>

          <motion.div {...fadeInUp}>
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-[#1a1a2e]/50 border border-white/5 rounded-xl px-6 data-[state=open]:border-[#3b82f6]/30 transition-colors"
                >
                  <AccordionTrigger className="text-[#f5f5f5] text-left font-medium hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#a1a1aa] pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Section Approfondir */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="approfondir-section">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.2em] text-[#3b82f6] font-bold mb-4">Approfondir</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight">
              Aller plus loin.
            </h2>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            variants={staggerChildren}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {[
              {
                to: '/perte-information-entreprise',
                title: 'Pourquoi les entreprises perdent de l\'information',
                icon: Layers,
              },
              {
                to: '/retrouver-information-entreprise',
                title: 'Retrouver une information instantanément',
                icon: FileSearch,
              },
              {
                to: '/clone-collaborateur-entreprise',
                title: 'Peut-on créer un clone collaborateur ?',
                icon: Brain,
              },
              {
                to: '/entreprise-interrogeable',
                title: 'Et si votre entreprise devenait interrogeable ?',
                icon: MessageCircle,
              },
            ].map((card) => (
              <motion.div key={card.to} variants={childVariant}>
                <Link
                  to={card.to}
                  className="group flex items-start gap-4 p-5 rounded-xl border border-white/5 hover:border-[#3b82f6]/30 bg-[#1a1a2e]/30 hover:bg-[#1a1a2e]/60 transition-all"
                  data-testid={`approfondir-card-${card.to.slice(1)}`}
                >
                  <card.icon className="w-5 h-5 text-[#3b82f6] mt-0.5 flex-shrink-0" />
                  <div className="flex-1 flex items-center justify-between gap-3">
                    <span className="text-[#f5f5f5] text-sm font-medium group-hover:text-[#3b82f6] transition-colors">
                      {card.title}
                    </span>
                    <ArrowRight className="w-4 h-4 text-[#71717a] group-hover:text-[#3b82f6] group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Final + Demo Form */}
      <section id="demo" className="py-24 md:py-32 px-6 md:px-12 bg-[#050505]" data-testid="demo-section">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#f5f5f5] tracking-tight mb-6">
              Arrêtez de chercher l'information.
              <br />
              <motion.span 
                className="text-[#3b82f6]"
                animate={{
                  textShadow: [
                    '0 0 10px rgba(59, 130, 246, 0.3)',
                    '0 0 25px rgba(59, 130, 246, 0.5)',
                    '0 0 10px rgba(59, 130, 246, 0.3)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                Commencez à l'utiliser.
              </motion.span>
            </h2>
            <p className="text-[#a1a1aa] max-w-xl mx-auto">
              15 minutes de démo. Zéro engagement. On vous montre avec vos propres données.
            </p>
            <p className="text-[#f5f5f5] font-medium mt-6">
              Interrogez la mémoire de votre entreprise.
            </p>
          </motion.div>

          <DemoForm />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
